import {
  faArrowUp,
  faExclamationTriangle,
  faSave,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import useNotification from 'hooks/useNotification';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Nav } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import paths from 'routes/paths';
import ProfilesService from 'services/Profiles.service';
import {
  ADVANCED_OPERATION_PERMISSIONS,
  BASIC_OPERATION_PERMISSIONS,
  INTERMEDIATE_OPERATION_PERMISSIONS
} from './profileModels';

const ProfileEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [profileName, setProfileName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionsToAdd, setPermissionsToAdd] = useState({ permissions: [] });
  const [permissionsToRemove, setPermissionsToRemove] = useState({
    permissions: []
  });
  const [activeTab, setActiveTab] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('-');
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const permissionsPromise = ProfilesService.listPermissions(id);

        let profileData = null;

        // Priority 1: Get from navigation state
        if (location.state && location.state.profile) {
          profileData = location.state.profile;
        } else {
          // Priority 2: Fetch list and find (fallback for direct URL access)
          // Fetching page 0 with sufficient size to find the profile
          const profilesResponse = await ProfilesService.list(
            'page=0&size=100'
          );
          if (profilesResponse && profilesResponse.content) {
            profileData = profilesResponse.content.find(
              p => p.id === Number(id)
            );
          }
        }

        const permissionsResponse = await permissionsPromise;

        if (profileData) {
          setProfileName(profileData.name);
          setIsAdmin(profileData.isAdmin);
        } else if (permissionsResponse && permissionsResponse.name) {
          // Fallback if list fetch fails or not found (shouldn't happen for valid ID)
          setProfileName(permissionsResponse.name);
          // We can't determine isAdmin from permissions response reliably if not in type,
          // but keeping it safe (false) or maybe it's passed?
          // Assuming name is at least there.
        }

        if (permissionsResponse) {
          setPermissions(permissionsResponse);
          if (permissionsResponse.length > 0) {
            setActiveTab(permissionsResponse[0].id);
          }
        }
      } catch (error) {
        console.error(error);
        showErrorNotification('Erro ao carregar dados do perfil.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, location.state]);

  useEffect(() => {
    const initialSelected = [];
    permissions.forEach(module => {
      module.subModules.forEach(subModule => {
        subModule.permissions.forEach(p => {
          if (p.associated) {
            initialSelected.push(p.id);
          }
        });
      });
    });
    setSelectedPermissions(initialSelected);
  }, [permissions]);

  useEffect(() => {
    if (selectedTemplate === '-') return;
    switch (selectedTemplate) {
      case 'basic':
        applyTemplate(BASIC_OPERATION_PERMISSIONS);
        break;
      case 'intermediate':
        applyTemplate(INTERMEDIATE_OPERATION_PERMISSIONS);
        break;
      case 'advanced':
        applyTemplate(ADVANCED_OPERATION_PERMISSIONS);
        break;
      default:
        break;
    }
  }, [selectedTemplate]);

  const togglePermission = item => {
    if (isAdmin) return;

    let newSelected = [...selectedPermissions];
    let newToAdd = [...permissionsToAdd.permissions];
    let newToRemove = [...permissionsToRemove.permissions];

    if (selectedPermissions.includes(item.id)) {
      newSelected = newSelected.filter(pid => pid !== item.id);
      if (item.associated) {
        newToRemove.push(parseInt(item.id));
      }
      newToAdd = newToAdd.filter(pid => pid !== item.id);
    } else {
      newSelected.push(item.id);
      if (!item.associated) {
        newToAdd.push(parseInt(item.id));
      }
      newToRemove = newToRemove.filter(pid => pid !== item.id);
    }

    setSelectedPermissions(newSelected);
    setPermissionsToAdd({ permissions: newToAdd });
    setPermissionsToRemove({ permissions: newToRemove });
  };

  const toggleSubmodule = subModule => {
    if (isAdmin) return;

    const subModulePermissionIds = subModule.permissions.map(p => p.id);
    const allSelected = subModulePermissionIds.every(pid =>
      selectedPermissions.includes(pid)
    );

    let newSelected = [...selectedPermissions];
    let newToAdd = [...permissionsToAdd.permissions];
    let newToRemove = [...permissionsToRemove.permissions];

    subModule.permissions.forEach(item => {
      if (allSelected) {
        if (newSelected.includes(item.id)) {
          newSelected = newSelected.filter(pid => pid !== item.id);
          if (item.associated) {
            if (!newToRemove.includes(parseInt(item.id))) {
              newToRemove.push(parseInt(item.id));
            }
          }
          newToAdd = newToAdd.filter(pid => pid !== item.id);
        }
      } else {
        if (!newSelected.includes(item.id)) {
          newSelected.push(item.id);
          if (!item.associated) {
            if (!newToAdd.includes(parseInt(item.id))) {
              newToAdd.push(parseInt(item.id));
            }
          }
          newToRemove = newToRemove.filter(pid => pid !== item.id);
        }
      }
    });

    setSelectedPermissions(newSelected);
    setPermissionsToAdd({ permissions: newToAdd });
    setPermissionsToRemove({ permissions: newToRemove });
  };

  const applyTemplate = templatePermissions => {
    if (isAdmin) return;

    // Find permission IDs that match the template names
    const targetPermissionIds = [];

    permissions.forEach(module => {
      module.subModules.forEach(subModule => {
        subModule.permissions.forEach(p => {
          if (templatePermissions.includes(p.name)) {
            targetPermissionIds.push(p.id);
          }
        });
      });
    });

    let newSelected = [...targetPermissionIds];
    let newToAdd = [...permissionsToAdd.permissions];
    let newToRemove = [...permissionsToRemove.permissions];

    // Determine what to add and remove based on the *new* selection target
    // We want to SET the permissions to exactly this template, or just ADD/MERGE?
    // "Quando clicar nesse botão, deve selecionar as permissões" usually means setting or selecting them.
    // If I select a template, usually it replaces current selection or adds to it.
    // Let's implement as "Set selection to match template exactly" effectively resetting and applying.

    // Actually, to be safe and clear, let's just make sure all these are selected.
    // If a permission is NOT in the template, should it be deselected?
    // Usually templates act as a "preset". I will replace the current selection with the template selection.

    // Calculate changes
    // 1. permissions currently associated (p.associated) that are NOT in target -> remove
    // 2. target permissions that are NOT currently associated -> add

    // Reset change tracking
    newToAdd = [];
    newToRemove = [];

    const allPermissions = [];
    permissions.forEach(module => {
      module.subModules.forEach(subModule => {
        subModule.permissions.forEach(p => {
          allPermissions.push(p);
        });
      });
    });

    allPermissions.forEach(p => {
      const shouldBeSelected = targetPermissionIds.includes(p.id);

      if (shouldBeSelected) {
        if (!p.associated) {
          newToAdd.push(p.id);
        }
      } else {
        if (p.associated) {
          newToRemove.push(p.id);
        }
      }
    });

    setSelectedPermissions(targetPermissionIds);
    setPermissionsToAdd({ permissions: newToAdd });
    setPermissionsToRemove({ permissions: newToRemove });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (isAdmin) return;

    setIsLoading(true);
    try {
      await ProfilesService.edit(id, { name: profileName });

      if (permissionsToAdd.permissions.length > 0) {
        await ProfilesService.associatePermissions(id, permissionsToAdd);
      }
      if (permissionsToRemove.permissions.length > 0) {
        await ProfilesService.unassociatePermissions(id, permissionsToRemove);
      }

      showSuccessNotification('Perfil atualizado com sucesso!');

      // Refresh permissions
      const permissionsResponse = await ProfilesService.listPermissions(id);

      // Update name if returned (or keep current)
      if (permissionsResponse && permissionsResponse.name) {
        setProfileName(permissionsResponse.name);
      }

      setPermissions(permissionsResponse);
      setPermissionsToAdd({ permissions: [] });
      setPermissionsToRemove({ permissions: [] });

      navigate(paths.profileRegistration, { replace: true });
    } catch (error) {
      console.error(error);
      showErrorNotification('Erro ao salvar as alterações.');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToSection = id => {
    setActiveTab(id);
    const element = document.getElementById(`module-${id}`);
    if (element) {
      // Offset for sticky header/nav
      const offset = 180;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (isLoading && !profileName) {
    return (
      <FalconComponentCard>
        <FalconComponentCard.Body>
          <Skeleton count={5} />
        </FalconComponentCard.Body>
      </FalconComponentCard>
    );
  }

  return (
    <>
      <FalconComponentCard>
        <Card.Header>
          <Flex alignItems="center" justifyContent="start">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="arrow-left"
              onClick={() => navigate(-1)}
              className="me-2"
            />

            <Card.Title className="mb-0">Editar Perfil</Card.Title>
          </Flex>
        </Card.Header>
        <FalconComponentCard.Body>
          {isAdmin && (
            <Alert variant="warning" className="mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Este é um perfil de administrador e não pode ser editado.
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <fieldset disabled={isAdmin}>
              <Form.Group className="mb-4" controlId="profileName">
                <Form.Label>Nome do Perfil</Form.Label>
                <Form.Control
                  type="text"
                  value={profileName}
                  onChange={e => setProfileName(e.target.value)}
                  required
                  className="w-25"
                />
              </Form.Group>

              <div className="mb-4">
                <h5 className="mb-3">Modelos de Permissão</h5>
                <div>
                  <Form.Check
                    type="radio"
                    id="template-basic"
                    name="template"
                    label="Operação Básica"
                    value="basic"
                    checked={selectedTemplate === 'basic'}
                    onChange={e => setSelectedTemplate(e.target.value)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="template-intermediate"
                    name="template"
                    label="Operação Intermediária"
                    value="intermediate"
                    checked={selectedTemplate === 'intermediate'}
                    onChange={e => setSelectedTemplate(e.target.value)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="template-advanced"
                    name="template"
                    label="Operação Avançada"
                    value="advanced"
                    checked={selectedTemplate === 'advanced'}
                    onChange={e => setSelectedTemplate(e.target.value)}
                    className="mb-2"
                  />
                </div>
                {/* <Flex alignItems="center" gap={2}>
                  <Button
                    variant="falcon-default"
                    onClick={() => applyTemplate(BASIC_OPERATION_PERMISSIONS)}
                    className="me-3"
                  >
                    <FontAwesomeIcon icon={faUserTie} className="me-2" />
                    Operação Básica
                  </Button>
                  <Button
                    variant="falcon-default"
                    onClick={() =>
                      applyTemplate(INTERMEDIATE_OPERATION_PERMISSIONS)
                    }
                    className="me-3"
                  >
                    <FontAwesomeIcon icon={faUserCog} className="me-2" />
                    Operação Intermediária
                  </Button>
                  <Button
                    variant="falcon-default"
                    onClick={() =>
                      applyTemplate(ADVANCED_OPERATION_PERMISSIONS)
                    }
                  >
                    <FontAwesomeIcon icon={faUserShield} className="me-2" />
                    Operação Avançada
                  </Button>
                </Flex> */}
              </div>

              <div className="d-flex align-items-center mb-2">
                <h5 className="mb-0 me-3">Permissões</h5>
                <div
                  className="flex-grow-1 overflow-auto"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <Nav
                    variant="pills"
                    className="flex-nowrap"
                    style={{ fontSize: '0.9rem' }}
                  >
                    {permissions.map(module => (
                      <Nav.Item key={module.id}>
                        <Nav.Link
                          active={activeTab === module.id}
                          onClick={() => scrollToSection(module.id)}
                          className="cursor-pointer py-1 px-3"
                          disabled={false}
                        >
                          {module.name}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </div>
              </div>
              <hr className="mt-0 mb-4" />

              <div className="permissions-container">
                {permissions.map((module, moduleIndex) => (
                  <div
                    key={module.id}
                    id={`module-${module.id}`}
                    className={classNames('py-3', {
                      'border-bottom': moduleIndex !== permissions.length - 1
                    })}
                  >
                    <div className="mb-3">
                      <h5 className="text-800">{module.name}</h5>
                    </div>

                    <div className="ps-0">
                      {module.subModules.map((subModule, subIndex) => {
                        const subModulePermissionIds =
                          subModule.permissions.map(p => p.id);
                        const isAllSelected = subModulePermissionIds.every(
                          pid => selectedPermissions.includes(pid)
                        );
                        const isSomeSelected = subModulePermissionIds.some(
                          pid => selectedPermissions.includes(pid)
                        );

                        return (
                          <div
                            key={subModule.id}
                            className={classNames('mb-4 ms-3', {
                              'mb-lg-5':
                                subIndex !== module.subModules.length - 1
                            })}
                          >
                            <h6 className="fw-bold mb-2 text-900">
                              {subModule.name}
                            </h6>

                            {/* Submodule Select All */}
                            <div className="mb-2 form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isAllSelected}
                                disabled={isAdmin}
                                ref={input => {
                                  if (input) {
                                    input.indeterminate =
                                      isSomeSelected && !isAllSelected;
                                  }
                                }}
                                onChange={() => toggleSubmodule(subModule)}
                                id={`submodule-${subModule.id}`}
                              />
                              <label
                                className="form-check-label text-700"
                                htmlFor={`submodule-${subModule.id}`}
                              >
                                Selecionar todos
                              </label>
                            </div>

                            {/* Permissions List */}
                            <div className="ms-4">
                              {subModule.permissions.map(permission => (
                                <div
                                  key={permission.id}
                                  className="form-check mb-1"
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedPermissions.includes(
                                      permission.id
                                    )}
                                    disabled={isAdmin}
                                    onChange={() =>
                                      togglePermission(permission)
                                    }
                                    id={`perm-${permission.id}`}
                                  />
                                  <label
                                    className="form-check-label text-break"
                                    htmlFor={`perm-${permission.id}`}
                                  >
                                    {permission.description}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex justify-content-between mt-4 sticky-bottom bg-body-emphasis py-3 border-top">
                <Button
                  variant="falcon-default"
                  onClick={scrollToTop}
                  className="me-auto"
                  size="sm"
                  title="Voltar ao topo"
                  disabled={false}
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </Button>

                <Flex>
                  <Button
                    variant="falcon-default"
                    onClick={() => navigate(-1)}
                    className="me-2"
                    disabled={false}
                  >
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isLoading || isAdmin}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Salvar
                  </Button>
                </Flex>
              </div>
            </fieldset>
          </Form>
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default ProfileEdit;
